import {
    BookUser,
    Bot,
    Clock,
    Cpu,
    MessageCircle,
    Mic2,
    Send,
    Settings,
    ShieldAlert,
    Smartphone,
    Store,
    Tag,
    Users,
    Volume2,
    Webhook,
    Aperture,
    Bell,
    BarChart3,
    UserCog,
    UserCircle,
    UserPlus,
    Zap,
} from 'lucide-react'
import {DEFAULT_TTS_VOICE_ID, DEFAULT_VOICE_CHANGER_MODEL_ID} from '@/shared'
import type {GroupAutoReplyConfig, GroupKeywordReplyRule, SettingsState} from '@/types'

export const menus = {
    conversation: {
        label: '对话',
        items: [
            {id: 'realtime', path: '/conversation/realtime', label: '实时对话', icon: MessageCircle},
        ],
    },
    message: {
        label: '自动化',
        items: [
            {id: 'auto-reply', path: '/message/auto-reply', label: '自动回复', icon: Bot},
            {id: 'scheduled', path: '/message/scheduled', label: '定时发送消息', icon: Clock},
            {id: 'mass', path: '/message/mass', label: '群发消息', icon: Send},
            {id: 'moments', path: '/message/moments', label: '朋友圈助手', icon: Aperture},
            {id: 'automation', path: '/message/automation', label: '自动化与安全机制', icon: Settings},
        ],
    },
    contact: {
        label: '好友管理',
        items: [
            {id: 'address-book', path: '/contact/address-book', label: '通讯录', icon: BookUser},
            {id: 'labels', path: '/contact/labels', label: '标签管理', icon: Tag},
            {id: 'check-status', path: '/contact/check-status', label: '检测好友关系', icon: ShieldAlert},
        ],
    },
    group: {
        label: '群聊管理',
        items: [
            {id: 'welcome', path: '/group/welcome', label: '入群欢迎语', icon: MessageCircle},
            {id: 'group-auto-reply', path: '/group/auto-reply', label: '群自动回复', icon: Bot},
            {id: 'members', path: '/group/members', label: '群成员列表', icon: Users},
        ],
    },
    settings: {
        label: '设置',
        items: [
            {id: 'personal', path: '/settings/personal', label: '微信实例', icon: Smartphone, group: '账号'},
            {id: 'user-profile', path: '/settings/user-profile', label: '用户信息', icon: UserCircle, group: '账号'},
            {id: 'ai-config', path: '/settings/ai-config', label: 'AI 大模型配置', icon: Cpu, group: '账号'},
            {id: 'voice-changer', path: '/settings/voice-changer', label: '变声器', icon: Mic2, group: '账号'},
            {id: 'tts-config', path: '/settings/tts-config', label: '文字转语音', icon: Volume2, group: '账号'},
            {id: 'webhook-trigger', path: '/settings/webhook-trigger', label: 'Webhook 触发器', icon: Webhook, group: '账号'},
            {id: 'quick-reply', path: '/settings/quick-reply', label: '快捷回复', icon: Zap, group: '账号'},
            {id: 'system', path: '/settings/system', label: '系统设置', icon: Bell, group: '账号'},
            {id: 'admin-stats', path: '/settings/admin-stats', label: '统计概览', icon: BarChart3, group: '管理员'},
            {id: 'user-manage', path: '/settings/user-manage', label: '用户管理', icon: UserCog, group: '管理员'},
            {id: 'registration-settings', path: '/settings/registration-settings', label: '注册设置', icon: UserPlus, group: '管理员'},
        ],
    },
    plugin: {
        label: '插件',
        items: [
            {id: 'plugin-market', path: '/plugin/market', label: '插件商城', icon: Store},
        ],
    },
}

export type MenuKey = keyof typeof menus

/**
 * Always return all menu items regardless of login status.
 */
export function menuItemsFor(key: MenuKey, _isLoggedIn: boolean) {
    void _isLoggedIn
    return menus[key].items
}

/**
 * Filter menu items based on user role.
 * Non-admin users should not see admin-only items like 'user-manage'.
 */
export function filterMenuItemsForRole<T extends {id: string}>(items: T[], role: string): T[] {
    if (role === 'admin') return items
    return items.filter((item) => item.id !== 'admin-stats' && item.id !== 'user-manage' && item.id !== 'registration-settings')
}

export const emptySettings: SettingsState = {
    aiConfig: {
        apiBaseUrl: 'https://api.openai.com/v1',
        apiKey: '',
        model: 'gpt-4o-mini',
    },
    aiReply: {
        aiEnabled: false,
        keywordEnabled: true,
        prompt: '你现在是我的私人助理，请用自然、简洁、礼貌的方式代我回复消息。',
        contactWxids: [],
        contactLabelIds: [],
    },
    security: {autoAcceptFriend: false, replyDelaySeconds: 2},
    system: {
        messageNotifications: false,
        conversationLayout: 'single',
        aiReplyHistoryLimit: 50,
        aiReplyPrompt: '你是我的微信聊天回复助手。请根据最近聊天记录，生成一条自然、简洁、贴合上下文的中文回复。语气要像我本人，避免过度热情，不要解释生成过程，只输出可以直接发送的回复正文。',
    },
    voiceChanger: {enabled: false, selectedModelId: DEFAULT_VOICE_CHANGER_MODEL_ID},
    tts: {enabled: false, selectedVoiceId: DEFAULT_TTS_VOICE_ID},
    proxy: {proxyUrl: '', proxyMode: 'custom', portNestId: '', proxyIp: '', proxyUser: '', proxyPassword: ''},
    groupWelcome: {},
    groupAutoReply: {},
}

export const defaultGroupAutoReplyPrompt = '请根据群聊消息自然、简洁地回复。'

export function createGroupKeywordRule(): GroupKeywordReplyRule {
    return {
        id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
        enabled: true,
        keyword: '',
        reply: '',
        replyType: 'text',
        replyPayload: {type: 'text', content: ''},
        matchType: 'exact',
    }
}

export function normalizeGroupAutoReplyConfig(config?: Partial<GroupAutoReplyConfig> | null): GroupAutoReplyConfig {
    const keywordRules: GroupKeywordReplyRule[] = Array.isArray(config?.keywordRules) && config.keywordRules.length
        ? config.keywordRules.map((rule) => ({
            id: rule.id || `${Date.now()}-${Math.random().toString(16).slice(2)}`,
            enabled: rule.enabled !== false,
            keyword: rule.keyword || '',
            reply: rule.reply || '',
            replyType: rule.replyType || 'text',
            replyPayload: rule.replyPayload || {type: rule.replyType || 'text', content: rule.reply || ''},
            matchType:
                rule.matchType === 'fuzzy' || rule.matchType === 'regex'
                    ? rule.matchType
                    : 'exact',
        }))
        : config?.keyword || config?.reply
            ? [{
                id: 'legacy',
                enabled: true,
                keyword: config.keyword || '',
                reply: config.reply || '',
                replyType: 'text',
                replyPayload: {type: 'text', content: config.reply || ''},
                matchType: 'exact',
            }]
            : []

    // Derive independent toggles with backward compatibility with the old
    // mutually-exclusive `mode` field.
    const aiEnabled =
        typeof config?.aiEnabled === 'boolean'
            ? config.aiEnabled
            : config?.mode === 'ai'
    const keywordEnabled =
        typeof config?.keywordEnabled === 'boolean'
            ? config.keywordEnabled
            : config?.mode === 'keyword'
                ? true
                : false

    return {
        aiEnabled,
        keywordEnabled,
        keywordRules,
        prompt: config?.prompt || defaultGroupAutoReplyPrompt,
    }
}

export const UI_PARITY_TODOS = [
    'ai-config.test-connectivity',
    'group-welcome.insert-newcomer',
    'group-welcome.insert-rules',
    'conversation.new',
    'conversation.voice-record',
] as const

export type UIParityTodoId = (typeof UI_PARITY_TODOS)[number]

export function reportTodo(id: UIParityTodoId) {
    if (typeof console !== 'undefined' && typeof console.info === 'function') {
        console.info(`[ui-parity-todo] ${id}`)
    }
}
